import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { XCircle } from 'lucide-react';
import { t } from '../../utils/i18n';
import {
  formatDateTimeKST,
} from '../../utils/kstDateTime';
import { useAuth } from '../../context/AuthContext';
import { useApiEnvironment } from '../../context/ApiEnvironmentContext';
import {
  deletePushProductUpdate,
  deletePushRecommendation,
  getPushRecommendationHistories,
  getPushProductUpdateHistories,
  patchPushHistoryWithScheduleResult,
  postPushProductUpdateV2,
} from '../../api/pushAdmin';
import PaginatedTableCard from '../../components/PaginatedTableCard/PaginatedTableCard';
import FormField from '../../components/FormField/FormField';

const HISTORY_PAGE_SIZE_OPTIONS = [5, 10, 20, 50];
const DEFAULT_HISTORY_PAGE_SIZE = 10;
const HISTORY_SKELETON_WIDTHS = ['w-28', 'w-20', 'w-28', 'w-40', 'w-32', 'w-14', 'w-24', 'w-12', 'w-12', 'w-16'];

const SEND_MODE_RECIPIENT_LOCAL = 'recipient_local_time';
const isLocalMode = (mode) => mode === SEND_MODE_RECIPIENT_LOCAL || mode?.startsWith('announcement_');
const PUSH_LOCALES = ['en', 'ko', 'ja', 'zh-Hans', 'zh-Hant', 'es', 'fr', 'de', 'pt-BR', 'ru', 'ar', 'hi', 'id', 'vi', 'fil', 'tr'];

const STICKY_ACTIONS_TH = 'sticky right-0 z-10 bg-dark-card py-3 px-4 text-center text-dark-text-secondary font-medium text-sm shadow-[-4px_0_8px_rgba(0,0,0,0.25)]';

function stickyActionsTdClass(isHighlighted, canCancel) {
  return [
    'sticky right-0 z-10 py-3 px-4 text-center shadow-[-4px_0_8px_rgba(0,0,0,0.25)]',
    'group-hover:bg-dark-surface/50',
    isHighlighted ? 'bg-primary/5 group-hover:bg-primary/10' : 'bg-dark-card',
    canCancel ? 'border-l border-sky-500/25' : '',
  ].filter(Boolean).join(' ');
}

function mapPushError(err) {
  if (err?.code === -4401 || err?.status === 403) {
    return t('push.errors.tokenExpired');
  }
  return err?.message || t('push.errors.generic');
}

function mapCancelError(err) {
  if (err?.status === 404) {
    return t('push.schedule.cancelError.notFound');
  }
  if (err?.status === 409) {
    return t('push.schedule.cancelError.notCancellable');
  }
  return mapPushError(err);
}

function progressPercent(completed, target) {
  const tgt = Number(target);
  const cmp = Number(completed);
  if (!Number.isFinite(tgt) || tgt <= 0 || !Number.isFinite(cmp)) return 0;
  return Math.min(100, Math.round((cmp / tgt) * 100));
}

function MiniProgressBar({ percent }) {
  const p = Math.max(0, Math.min(100, percent));
  return (
    <div className="flex items-center gap-2 min-w-[7rem]">
      <div className="flex-1 h-1.5 rounded-full bg-dark-border overflow-hidden">
        <div
          className="h-full rounded-full bg-primary transition-[width] duration-300"
          style={{ width: `${p}%` }}
        />
      </div>
      <span className="text-xs text-dark-text-secondary tabular-nums w-9 text-right">{p}%</span>
    </div>
  );
}

function StatusChip({ status }) {
  const styles = {
    scheduled: 'bg-sky-500/15 text-sky-300 border-sky-500/35',
    sending: 'bg-amber-500/15 text-amber-300 border-amber-500/35',
    sent: 'bg-green-500/15 text-green-300 border-green-500/35',
    cancelled: 'bg-gray-500/15 text-gray-300 border-gray-500/35',
    failed: 'bg-red-500/15 text-red-300 border-red-500/35',
  };
  const cls = styles[status] ?? 'bg-dark-surface text-dark-text-secondary border-dark-border';
  const labelKey = `push.schedule.status.${status}`;
  const label = status && t(labelKey) !== labelKey ? t(labelKey) : (status || '-');

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${cls}`}>
      {label}
    </span>
  );
}

function truncateText(text, max = 48) {
  const s = String(text ?? '');
  if (s.length <= max) return s;
  return `${s.slice(0, max)}…`;
}

const textareaClassName = 'w-full rounded-lg border border-dark-border bg-dark-bg text-dark-text p-3 text-sm focus:border-primary/60 focus:ring-1 focus:ring-primary/35 outline-none';

export default function PushSchedulePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { role, token, logout } = useAuth();
  const { apiEnvironment, registerEnvironmentGuard } = useApiEnvironment();
  const isAdmin = role === 'admin';

  const [composerOpen, setComposerOpen] = useState(false);
  const [body, setBody] = useState('');
  const [title, setTitle] = useState('');
  const [includeInbox, setIncludeInbox] = useState(false);
  const [windowStart, setWindowStart] = useState('09:00');
  const [windowEnd, setWindowEnd] = useState('18:00');
  const [confirmation, setConfirmation] = useState('');
  const [historyKind, setHistoryKind] = useState('announcement');
  const [activeContentLocale, setActiveContentLocale] = useState('en');
  const [localizedContent, setLocalizedContent] = useState({});
  const [recipientLocalDate, setRecipientLocalDate] = useState('');
  const [recipientLocalTime, setRecipientLocalTime] = useState('13:00');
  const [validationError, setValidationError] = useState('');
  const [scheduleResult, setScheduleResult] = useState(null);
  const [scheduleError, setScheduleError] = useState(null);
  const [lastCampaignId, setLastCampaignId] = useState(null);
  const [cancelSuccessMsg, setCancelSuccessMsg] = useState('');
  const [cancelErrorMsg, setCancelErrorMsg] = useState('');
  const [cancellingId, setCancellingId] = useState(null);

  const [historyPage, setHistoryPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_HISTORY_PAGE_SIZE);

  const historiesQuery = useQuery({
    queryKey: ['pushProductUpdateHistories', apiEnvironment, historyKind, historyPage, rowsPerPage],
    queryFn: () => (historyKind === 'recommendation' ? getPushRecommendationHistories : getPushProductUpdateHistories)(historyPage, rowsPerPage, token),
    enabled: !!token,
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    setHistoryPage(1);
  }, [rowsPerPage, apiEnvironment, historyKind]);

  useEffect(() => {
    const err = historiesQuery.error;
    if (err?.status === 401) {
      logout();
      navigate('/login', { replace: true });
    }
  }, [historiesQuery.error, logout, navigate]);

  const handleAuthError = (err) => {
    if (err?.status === 401 || err?.code === -4301) {
      logout();
      navigate('/login', { replace: true });
    }
  };

  const scheduleMutation = useMutation({
    mutationFn: (payload) => postPushProductUpdateV2(payload, token),
    onSuccess: async (data) => {
      setComposerOpen(false);
      setHistoryKind('announcement');
      setScheduleResult(data);
      setScheduleError(null);
      if (data?.campaignId) {
        setLastCampaignId(data.campaignId);
        setHistoryPage(1);
      }
      await queryClient.invalidateQueries({ queryKey: ['pushProductUpdateHistories'] });
      if (data?.campaignId) {
        queryClient.setQueriesData(
          { queryKey: ['pushProductUpdateHistories'] },
          (old) => patchPushHistoryWithScheduleResult(old, data),
        );
      }
    },
    onError: (err) => {
      setScheduleError(err);
      setScheduleResult(null);
      handleAuthError(err);
    },
  });

  const cancelMutation = useMutation({
    mutationFn: (campaignId) => (historyKind === 'recommendation' ? deletePushRecommendation : deletePushProductUpdate)(campaignId, token),
    onSuccess: () => {
      setCancelSuccessMsg(t('push.schedule.cancelSuccess'));
      setCancelErrorMsg('');
      queryClient.invalidateQueries({ queryKey: ['pushProductUpdateHistories'] });
    },
    onError: (err) => {
      setCancelErrorMsg(mapCancelError(err));
      setCancelSuccessMsg('');
      handleAuthError(err);
    },
    onSettled: () => {
      setCancellingId(null);
    },
  });

  const {
    items: historyItems = [],
    total_count: totalCountSnake,
    totalCount: totalCountCamel,
    page: apiPage = historyPage,
    page_size: pageSizeSnake,
    pageSize: pageSizeCamel,
    total_pages: totalPagesSnake,
    totalPages: totalPagesCamel,
  } = historiesQuery.data || {};

  const totalCount = totalCountCamel ?? totalCountSnake ?? 0;
  const apiPageSize = pageSizeCamel ?? pageSizeSnake ?? rowsPerPage;
  const totalPages = totalPagesCamel ?? totalPagesSnake
    ?? Math.max(1, Math.ceil(totalCount / apiPageSize));

  const tableMinHeightPx = 56 + rowsPerPage * 52;
  const listError = historiesQuery.isError ? historiesQuery.error?.message : null;

  useEffect(() => registerEnvironmentGuard?.(() => !scheduleMutation.isPending && !cancelMutation.isPending), [registerEnvironmentGuard, scheduleMutation.isPending, cancelMutation.isPending]);
  const confirmationValue = `${apiEnvironment.toUpperCase()}:announcement`;
  useEffect(() => { setComposerOpen(false); setTitle(''); setBody(''); setLocalizedContent({}); setConfirmation(''); setScheduleResult(null); setScheduleError(null); }, [apiEnvironment]);
  const validateForm = () => {
    if (!title.trim()) return t('push.schedule.validation.title');
    if (!body.trim()) return t('push.schedule.validation.body');
    for (const [locale, content] of Object.entries(localizedContent)) {
      if ((content?.title?.trim() || content?.body?.trim()) && (!content?.title?.trim() || !content?.body?.trim())) {
        return t('push.schedule.validation.localizationIncomplete', { locale });
      }
    }
    if (!recipientLocalDate || !recipientLocalTime) return t('push.schedule.validation.recipientLocalTime');
    if (new Date(`${recipientLocalDate}T${recipientLocalTime}:00+14:00`) <= new Date()) return t('push.schedule.validation.recipientLocalTimePast');
    if (!windowStart || !windowEnd || windowStart >= windowEnd || recipientLocalTime < windowStart || recipientLocalTime >= windowEnd) return t('push.schedule.validation.window');
    if (confirmation !== confirmationValue) return t('push.schedule.validation.confirmation');
    return '';
  };

  const handleSchedule = () => {
    setValidationError('');
    setScheduleResult(null);
    setScheduleError(null);
    setCancelSuccessMsg('');
    setCancelErrorMsg('');

    const errMsg = validateForm();
    if (errMsg) {
      setValidationError(errMsg);
      return;
    }

    const payload = {
      announcement: true, confirmation, title: title.trim(), body: body.trim(), includeInbox,
      deliveryWindow: { start: windowStart, end: windowEnd },
      localizations: {
        en: { title: title.trim(), body: body.trim() },
        ...Object.fromEntries(Object.entries(localizedContent)
          .filter(([, content]) => content?.title?.trim() && content?.body?.trim())
          .map(([locale, content]) => [locale, { title: content.title.trim(), body: content.body.trim() }])),
      },
      deliveryMode: SEND_MODE_RECIPIENT_LOCAL,
      localDate: recipientLocalDate, localTime: recipientLocalTime, fallbackTimezone: 'UTC',
    };
    scheduleMutation.mutate(payload);
  };

  const handleCancel = (campaignId) => {
    if (!window.confirm(t('push.schedule.cancelConfirm'))) return;
    setCancelSuccessMsg('');
    setCancelErrorMsg('');
    setCancellingId(campaignId);
    cancelMutation.mutate(campaignId);
  };

  const submitLabel = t('push.schedule.submitRecipientLocal');
  const activeBody = activeContentLocale === 'en' ? body : (localizedContent[activeContentLocale]?.body ?? '');
  const activeTitle = activeContentLocale === 'en' ? title : (localizedContent[activeContentLocale]?.title ?? '');
  const updateLocalizedField = (field, value) => {
    if (activeContentLocale === 'en') {
      if (field === 'body') setBody(value);
      else setTitle(value);
      return;
    }
    setLocalizedContent((current) => ({
      ...current,
      [activeContentLocale]: { ...current[activeContentLocale], [field]: value },
    }));
  };

  return (
    <div className="min-h-screen py-8 px-3 sm:px-5 lg:px-6 bg-dark-bg">
      <div className="w-full max-w-[min(100%,1200px)] mx-auto space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div><h2 className="text-xl font-bold text-dark-text">{t('push.operations.campaignList')}</h2>
          <p className="text-dark-text-secondary text-sm mt-1">{t('push.operations.campaignBoundary')}</p></div>
          <button type="button" className="rounded-lg border border-dark-border px-3 py-2 text-sm" disabled={!isAdmin || scheduleMutation.isPending} onClick={() => setComposerOpen(v => !v)}>{t(`push.operations.${composerOpen ? 'closeComposer' : 'writeCampaign'}`)}</button>
        </div>

        {!isAdmin && <p className="text-sm text-dark-text-secondary">{t('push.operations.adminRequired')}</p>}
        <div hidden={!composerOpen} className="rounded-xl border border-dark-border bg-gradient-to-br from-dark-card to-dark-surface/40 p-4 sm:p-5 shadow-sm space-y-4">
          <p className="text-sm text-dark-text-secondary">{t('push.operations.draftUnavailable')}</p>
          <FormField
            label={t('push.schedule.localeLabel')}
            required
            hint={t('push.schedule.localeHint')}
          >
            <select
              value={activeContentLocale}
              onChange={(e) => setActiveContentLocale(e.target.value)}
              className="w-full rounded-lg border border-dark-border bg-dark-bg text-dark-text p-3 text-sm outline-none focus:border-primary/60"
            >
              {PUSH_LOCALES.map((locale) => {
                const content = locale === 'en' ? { body, title } : localizedContent[locale];
                const complete = content?.body?.trim() && content?.title?.trim();
                return <option key={locale} value={locale}>{locale}{complete ? ' ✓' : ''}</option>;
              })}
            </select>
          </FormField>

          <FormField
            label={`${t('push.schedule.titleLabel')} (${activeContentLocale})`}
            required={activeContentLocale === 'en'}
            hint={t('push.schedule.titleHint')}
          >
            <textarea
              aria-label={`${t('push.schedule.titleLabel')} (${activeContentLocale})`}
              value={activeTitle}
              onChange={(e) => {
                updateLocalizedField('title', e.target.value);
                setValidationError('');
              }}
              rows={3}
              className={textareaClassName}
              placeholder={t('push.schedule.titlePlaceholder')}
            />
          </FormField>

          <div>
            <label className="block text-xs font-medium tracking-wide text-dark-text-secondary uppercase mb-2">
              {t('push.schedule.bodyLabel')} ({activeContentLocale})
            </label>
            <textarea
              aria-label={`${t('push.schedule.bodyLabel')} (${activeContentLocale})`}
              value={activeBody}
              onChange={(e) => {
                updateLocalizedField('body', e.target.value);
                setValidationError('');
              }}
              rows={4}
              className={textareaClassName}
              placeholder={t('push.schedule.bodyPlaceholder')}
            />
          </div>

          <div className="space-y-3">
            <p className="text-sm text-dark-text">{t('push.schedule.mode.recipientLocal')}</p>
            <p className="text-xs text-dark-text-secondary">{t('push.schedule.mode.recipientLocalHint')}</p>
          </div>
            <div className="space-y-3">
              <p className="block text-xs font-medium tracking-wide text-dark-text-secondary uppercase">
                {t('push.schedule.recipientLocalLabel')}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input
                  type="date"
                  aria-label={t('push.schedule.localDateLabel')}
                  value={recipientLocalDate}
                  onChange={(e) => { setRecipientLocalDate(e.target.value); setValidationError(''); }}
                  className="rounded-lg border border-dark-border bg-dark-bg text-dark-text p-3 text-sm outline-none focus:border-primary/60"
                />
                <input
                  type="time"
                  aria-label={t('push.schedule.localTimeLabel')}
                  value={recipientLocalTime}
                  onChange={(e) => { setRecipientLocalTime(e.target.value); setValidationError(''); }}
                  className="rounded-lg border border-dark-border bg-dark-bg text-dark-text p-3 text-sm outline-none focus:border-primary/60"
                />
              </div>
              <p className="text-xs text-amber-300/90">{t('push.schedule.recipientLocalWarning')}</p>
            </div>
          <FormField label={t('push.schedule.windowLabel')} hint={t('push.schedule.windowHint')}>
            <div className="grid grid-cols-2 gap-3">
              <input aria-label={t('push.schedule.windowStart')} type="time" value={windowStart} onChange={(e) => setWindowStart(e.target.value)} className={textareaClassName} />
              <input aria-label={t('push.schedule.windowEnd')} type="time" value={windowEnd} onChange={(e) => setWindowEnd(e.target.value)} className={textareaClassName} />
            </div>
          </FormField>
          <label className="flex items-center gap-2 text-sm text-dark-text">
            <input type="checkbox" checked={includeInbox} onChange={(e) => setIncludeInbox(e.target.checked)} />
            {t('push.schedule.includeInbox')}
          </label>
          <p className="text-xs text-dark-text-secondary">{t(includeInbox ? 'push.schedule.inboxOnHint' : 'push.schedule.inboxOffHint')}</p>
          <FormField label={t('push.schedule.confirmationLabel')} hint={t('push.schedule.confirmationHint', { environment: apiEnvironment.toUpperCase(), value: confirmationValue })}>
            <input aria-label={t('push.schedule.confirmationLabel')} value={confirmation} onChange={(e) => setConfirmation(e.target.value)} className={textareaClassName} autoComplete="off" />
          </FormField>

          <div className="rounded-lg border border-dark-border bg-dark-bg p-4"><p className="text-xs text-dark-text-secondary">{t('push.operations.campaignPreview')}</p><p className="mt-2 font-medium">{activeTitle || 'Fancian'}</p><p className="mt-1 whitespace-pre-wrap break-words text-sm">{activeBody || '—'}</p></div>
          {validationError && <p className="text-sm text-red-400">{validationError}</p>}

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={handleSchedule}
              disabled={!isAdmin || scheduleMutation.isPending}
              className="px-4 py-2 rounded-lg bg-primary text-black text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed hover:bg-primary/90 transition-colors"
            >
              {scheduleMutation.isPending ? t('common.loading') : submitLabel}
            </button>
            {!isAdmin && (
              <p className="text-xs text-dark-text-secondary">{t('push.sendDisabled')}</p>
            )}
          </div>
        </div>

        {scheduleResult && (
          <div className="rounded-xl border border-green-500/40 bg-gradient-to-br from-green-500/10 to-dark-card p-4 sm:p-5 space-y-4 shadow-sm">
            <p className="text-sm font-semibold text-green-400">{t('push.schedule.result.success')}</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <p className="text-xs text-dark-text-secondary">{t('push.schedule.result.campaignId')}</p>
                <p className="text-xs font-mono text-dark-text break-all mt-0.5">
                  {scheduleResult.campaignId ?? '-'}
                </p>
              </div>
              <div>
                <p className="text-xs text-dark-text-secondary">{t('push.schedule.result.scheduleAt')}</p>
                <p className="text-sm text-dark-text mt-0.5">
                  {isLocalMode(scheduleResult.deliveryMode)
                    ? `${scheduleResult.localDate} ${scheduleResult.localTime} (${t('push.schedule.recipientLocalSuffix')})`
                    : formatDateTimeKST(scheduleResult.scheduleAt)}
                </p>
              </div>
              <div>
                <p className="text-xs text-dark-text-secondary">{t('push.schedule.result.status')}</p>
                <div className="mt-1">
                  <StatusChip status={scheduleResult.status} />
                </div>
              </div>
            </div>
          </div>
        )}

        {scheduleError && (
          <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4">
            <p className="text-sm font-semibold text-red-400">{t('push.result.error')}</p>
            <p className="text-sm text-red-300/90 mt-1">{mapPushError(scheduleError)}</p>
          </div>
        )}

        {cancelSuccessMsg && (
          <div className="rounded-lg border border-green-500/30 bg-green-500/10 p-4">
            <p className="text-sm text-green-400">{cancelSuccessMsg}</p>
          </div>
        )}

        {cancelErrorMsg && (
          <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4">
            <p className="text-sm text-red-400">{cancelErrorMsg}</p>
          </div>
        )}

        {listError && (
          <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
            {listError}
          </div>
        )}

        <div className="space-y-3">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h3 className="text-sm font-semibold text-dark-text">{t('push.schedule.histories.title')}</h3>
              <select aria-label={t('push.schedule.historyKindLabel')} value={historyKind} onChange={(e) => setHistoryKind(e.target.value)} className={textareaClassName}>
                <option value="announcement">{t('push.schedule.historyAnnouncements')}</option>
                <option value="recommendation">{t('push.schedule.historyRecommendations')}</option>
              </select>
              <p className="text-xs text-dark-text-secondary mt-0.5">
                {t('push.schedule.histories.description')}
              </p>
            </div>
            <button
              type="button"
              onClick={() => historiesQuery.refetch()}
              disabled={historiesQuery.isFetching}
              className="px-3 py-1.5 text-xs rounded-lg border border-dark-border text-dark-text-secondary hover:border-primary hover:text-primary transition-colors disabled:opacity-50"
            >
              {historiesQuery.isFetching ? t('common.loading') : t('push.schedule.histories.refresh')}
            </button>
          </div>

          {lastCampaignId && (
            <p className="text-xs text-primary/90">{t('push.schedule.histories.justRegistered')}</p>
          )}

          <PaginatedTableCard
            error={historiesQuery.error}
          dataUpdatedAt={historiesQuery.dataUpdatedAt}
          isPending={historiesQuery.isPending}
            isFetching={historiesQuery.isFetching}
            isEmpty={!historiesQuery.isPending && historyItems.length === 0}
            skeletonRows={rowsPerPage}
            skeletonCellWidths={HISTORY_SKELETON_WIDTHS}
            colSpan={10}
            containerStyle={{ minHeight: `${tableMinHeightPx}px` }}
            tableClassName="w-full min-w-[1100px]"
            loadingLabel={t('push.schedule.histories.loading')}
            emptyLabel={t('push.schedule.histories.empty')}
            page={historyPage}
            totalPages={totalPages}
            currentPage={apiPage}
            onPageChange={setHistoryPage}
            prevLabel={t('push.pagination.prev')}
            nextLabel={t('push.pagination.next')}
            summary={t('push.pagination.page', { page: apiPage, totalPages })}
            pageSize={rowsPerPage}
            pageSizeOptions={HISTORY_PAGE_SIZE_OPTIONS}
            onPageSizeChange={setRowsPerPage}
            pageSizeLabel={t('push.pagination.rowsPerPage')}
            header={(
              <tr className="border-b border-dark-border">
                <th className="text-left py-3 px-4 text-dark-text-secondary font-medium text-sm">
                  {t('push.schedule.histories.columns.scheduleAt')}
                </th>
                <th className="text-left py-3 px-4 text-dark-text-secondary font-medium text-sm">
                  {t('push.schedule.histories.columns.status')}
                </th>
                <th className="text-left py-3 px-4 text-dark-text-secondary font-medium text-sm">
                  {t('push.schedule.histories.columns.createdAt')}
                </th>
                <th className="text-left py-3 px-4 text-dark-text-secondary font-medium text-sm">
                  {t('push.schedule.histories.columns.body')}
                </th>
                <th className="text-left py-3 px-4 text-dark-text-secondary font-medium text-sm">
                  {t('push.schedule.histories.columns.delivery')}
                </th>
                <th className="text-right py-3 px-4 text-dark-text-secondary font-medium text-sm">
                  {t('push.schedule.histories.columns.target')}
                </th>
                <th className="text-left py-3 px-4 text-dark-text-secondary font-medium text-sm">
                  {t('push.schedule.histories.columns.progress')}
                </th>
                <th className="text-right py-3 px-4 text-dark-text-secondary font-medium text-sm">
                  {t('push.schedule.histories.columns.completed')}
                </th>
                <th className="text-right py-3 px-4 text-dark-text-secondary font-medium text-sm">
                  {t('push.schedule.histories.columns.failed')}
                </th>
                <th className={STICKY_ACTIONS_TH}>
                  {t('push.schedule.histories.columns.actions')}
                </th>
              </tr>
            )}
          >
            {historyItems.map((row) => {
              const campaignId = row.campaignId ?? row.campaign_id;
              const status = row.status ?? '';
              const isHighlighted = lastCampaignId && campaignId === lastCampaignId;
              const target = row.targetDeviceCount ?? row.target_device_count ?? 0;
              const completed = row.completedCount ?? row.completed_count ?? 0;
              const failed = row.failedCount ?? row.failed_count ?? 0;
              const pct = progressPercent(completed, target);
              const canCancel = isAdmin && status === 'scheduled' && !historiesQuery.isPlaceholderData;

              return (
                <tr
                  key={campaignId ?? row.createdAt}
                  className={`group border-b border-dark-border last:border-0 hover:bg-dark-surface/50 ${
                    isHighlighted ? 'bg-primary/5' : ''
                  }`}
                >
                  <td className="py-3 px-4 text-dark-text-secondary text-sm whitespace-nowrap">
                    {isLocalMode(row.deliveryMode)
                      ? `${row.localDate} ${String(row.localTime).slice(0, 5)} (${t('push.schedule.recipientLocalSuffix')})`
                      : formatDateTimeKST(row.scheduleAt ?? row.schedule_at)}
                  </td>
                  <td className="py-3 px-4">
                    <StatusChip status={status} />
                  </td>
                  <td className="py-3 px-4 text-dark-text-secondary text-sm whitespace-nowrap">
                    {formatDateTimeKST(row.createdAt ?? row.created_at)}
                  </td>
                  <td className="py-3 px-4 text-dark-text text-sm max-w-[12rem]" title={row.body}>
                    <div className="font-medium">{row.title}</div>{truncateText(row.body, 56)}
                  </td>
                  <td className="py-3 px-4 text-dark-text-secondary text-sm max-w-[10rem]" >
                    {t(row.includeInbox !== false ? 'push.schedule.inboxOn' : 'push.schedule.inboxOff')}
                    {row.deliveryWindow && <div className="text-xs">{row.deliveryWindow.start}–{row.deliveryWindow.end}</div>}
                    {row.contentLocales?.length > 0 && <div className="text-xs">{row.contentLocales.join(', ')}</div>}
                  </td>
                  <td className="py-3 px-4 text-right text-dark-text text-sm tabular-nums">{target}</td>
                  <td className="py-3 px-4">
                    <MiniProgressBar percent={pct} />
                  </td>
                  <td className="py-3 px-4 text-right text-green-400 text-sm tabular-nums">{completed}</td>
                  <td className={`py-3 px-4 text-right text-sm tabular-nums ${
                    failed > 0 ? 'text-red-400' : 'text-dark-text-secondary'
                  }`}
                  >
                    {failed}
                  </td>
                  <td className={stickyActionsTdClass(isHighlighted, canCancel)}>
                    {canCancel ? (
                      <button
                        type="button"
                        onClick={() => handleCancel(campaignId)}
                        disabled={cancellingId === campaignId}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border border-red-500/60 bg-red-500/15 text-red-300 hover:bg-red-500/25 disabled:opacity-50 transition-colors"
                      >
                        <XCircle className="w-4 h-4 shrink-0" aria-hidden />
                        {cancellingId === campaignId ? t('common.loading') : t('push.schedule.cancel')}
                      </button>
                    ) : (
                      <span className="text-dark-text-secondary text-xs">-</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </PaginatedTableCard>
        </div>
      </div>
    </div>
  );
}
